/**
 * Vertrags-Sektion (Contract-Manager, N2-3) für einen einzelnen Mandanten.
 * Wird als Sektion in TenantDetailPage eingehängt (Muster: BrandingSection).
 *
 * Zeigt + verwaltet:
 *   - Status (active|suspended)         → PUT  /contract/status
 *   - Storage-Quota (Bytes, GB-Eingabe) → PUT  /contract/storage-quota
 *   - AI-Token-Quota + Verbrauch        → PUT  /contract/ai-tokens
 *   - S3-Buckets (anlegen/löschen)      → POST/DELETE /contract/buckets
 *
 * Der Server synthetisiert beim ersten Read einen provisioned:false-Default;
 * die erste mutierende Aktion legt die Vertragszeile an. Read-only-Operatoren
 * sehen alle mutierenden Felder deaktiviert.
 */
import { useEffect, useState } from 'react';
import api, { ContractView, extractApiError } from '../services/api';
import { labelForContractStatus } from '../services/status-labels';

interface Props {
  slug: string;
  isReadOnly: boolean;
}

const BYTES_PER_GB = 1024 * 1024 * 1024;

function gbFromBytes(bytes: number): string {
  if (bytes === 0) return '0';
  return (bytes / BYTES_PER_GB).toFixed(2);
}

export function ContractSection({ slug, isReadOnly }: Props) {
  const [loaded, setLoaded] = useState(false);
  const [contract, setContract] = useState<ContractView | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Eingabe-Felder
  const [storageGb, setStorageGb] = useState('');
  const [aiQuota, setAiQuota] = useState('');
  const [bucketName, setBucketName] = useState('');
  const [bucketPurpose, setBucketPurpose] = useState('');
  const [bucketRegion, setBucketRegion] = useState('');

  function applyContract(c: ContractView): void {
    setContract(c);
    setStorageGb(c.storageQuotaBytes === 0 ? '' : gbFromBytes(c.storageQuotaBytes));
    setAiQuota(c.aiTokenQuota === 0 ? '' : String(c.aiTokenQuota));
  }

  useEffect(() => {
    let cancelled = false;
    api.get<ContractView>(`/tenants/${slug}/contract`).then((r) => {
      if (cancelled) return;
      applyContract(r.data);
      setLoaded(true);
    }).catch(() => {
      if (!cancelled) { setErr('Vertrag konnte nicht geladen werden'); setLoaded(true); }
    });
    return () => { cancelled = true; };
  }, [slug]);

  async function run(fn: () => Promise<ContractView>): Promise<void> {
    if (isReadOnly) return;
    setBusy(true);
    setErr(null);
    try {
      applyContract(await fn());
    } catch (e) {
      setErr(extractApiError(e));
    } finally {
      setBusy(false);
    }
  }

  const setStatus = (status: 'active' | 'suspended') =>
    run(async () => (await api.put<ContractView>(`/tenants/${slug}/contract/status`, { status })).data);

  const saveStorage = () => {
    const gb = Number(storageGb);
    if (storageGb !== '' && (!Number.isFinite(gb) || gb < 0)) {
      setErr('Storage-Quota muss eine nicht-negative Zahl (GB) sein.');
      return;
    }
    const bytes = storageGb === '' ? 0 : Math.round(gb * BYTES_PER_GB);
    void run(async () => (await api.put<ContractView>(`/tenants/${slug}/contract/storage-quota`, { bytes })).data);
  };

  const saveAiQuota = () => {
    const q = Number(aiQuota);
    if (aiQuota !== '' && (!Number.isInteger(q) || q < 0)) {
      setErr('AI-Token-Quota muss eine nicht-negative Ganzzahl sein (0 = unbegrenzt).');
      return;
    }
    const quota = aiQuota === '' ? 0 : q;
    void run(async () => (await api.put<ContractView>(`/tenants/${slug}/contract/ai-tokens`, { quota })).data);
  };

  const addBucket = () => {
    if (bucketName.trim().length < 3 || bucketPurpose.trim() === '') {
      setErr('Bucket-Name (min. 3 Zeichen) und Zweck erforderlich.');
      return;
    }
    void run(async () => {
      const { data } = await api.post<ContractView>(`/tenants/${slug}/contract/buckets`, {
        name: bucketName.trim(),
        purpose: bucketPurpose.trim(),
        region: bucketRegion.trim() === '' ? undefined : bucketRegion.trim(),
      });
      setBucketName(''); setBucketPurpose(''); setBucketRegion('');
      return data;
    });
  };

  const removeBucket = (id: string, name: string) => {
    if (!window.confirm(`Bucket "${name}" entfernen?`)) return;
    void run(async () => (await api.delete<ContractView>(`/tenants/${slug}/contract/buckets/${id}`)).data);
  };

  if (!loaded) return <p className="text-slate-500 text-sm">Lade Vertrag…</p>;

  const c = contract;
  const aiRemaining = c?.aiTokensRemaining === null ? 'unbegrenzt' : String(c?.aiTokensRemaining ?? 0);

  return (
    <section className="mb-6">
      <h3 className="font-semibold mb-2">Vertrag (Contract-Manager)</h3>
      <p className="text-xs text-slate-500 mb-3">
        S3-Buckets, AI-Token- und Storage-Quota für diesen Mandanten. Bei
        ausgesetztem Vertrag wird keine neue Allokation mehr zugelassen.
      </p>

      {err && <div className="bg-red-100 text-red-700 p-3 rounded mb-3 text-sm">{err}</div>}
      {c && !c.provisioned && (
        <div className="bg-amber-50 text-amber-800 p-3 rounded mb-3 text-sm">
          Noch kein Vertrag angelegt. Die erste Aktion (Quota setzen / Bucket anlegen) erstellt ihn.
        </div>
      )}

      <div className="bg-white rounded shadow p-4 space-y-4">
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-600">Status:</span>
          <span className="badge bg-slate-200">{labelForContractStatus(c?.status ?? 'active')}</span>
          {c?.status === 'active' ? (
            <button onClick={() => setStatus('suspended')} disabled={busy || isReadOnly} className="btn-danger">
              Aussetzen
            </button>
          ) : (
            <button onClick={() => setStatus('active')} disabled={busy || isReadOnly} className="btn-primary">
              Aktivieren
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-slate-600 mb-1">Storage-Quota (GB, 0/leer = unbegrenzt)</label>
            <div className="flex items-center gap-2">
              <input
                type="number" min={0} step="0.01"
                value={storageGb}
                onChange={(e) => setStorageGb(e.target.value)}
                disabled={isReadOnly}
                placeholder="unbegrenzt"
                className="flex-1 px-2 py-1 rounded border font-mono text-sm"
              />
              <button onClick={saveStorage} disabled={busy || isReadOnly} className="btn-primary">Setzen</button>
            </div>
          </div>
          <div>
            <label className="block text-sm text-slate-600 mb-1">AI-Token-Quota (0/leer = unbegrenzt)</label>
            <div className="flex items-center gap-2">
              <input
                type="number" min={0} step="1"
                value={aiQuota}
                onChange={(e) => setAiQuota(e.target.value)}
                disabled={isReadOnly}
                placeholder="unbegrenzt"
                className="flex-1 px-2 py-1 rounded border font-mono text-sm"
              />
              <button onClick={saveAiQuota} disabled={busy || isReadOnly} className="btn-primary">Setzen</button>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Verbraucht: <span className="font-mono">{c?.aiTokensUsed ?? 0}</span> · Rest:{' '}
              <span className="font-mono">{aiRemaining}</span>
            </p>
          </div>
        </div>

        <div>
          <h4 className="text-sm font-semibold text-slate-600 mb-2">S3-Buckets</h4>
          <table className="w-full text-sm mb-3">
            <thead className="bg-slate-100 text-left">
              <tr>
                <th className="p-2">Name</th><th className="p-2">Region</th>
                <th className="p-2">Zweck</th><th className="p-2">Status</th><th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {(c?.buckets ?? []).map((b) => (
                <tr key={b.id} className="border-t">
                  <td className="p-2 font-mono">{b.name}</td>
                  <td className="p-2 font-mono text-slate-500">{b.region}</td>
                  <td className="p-2">{b.purpose}</td>
                  <td className="p-2"><span className="badge bg-slate-200">{b.status}</span></td>
                  <td className="p-2">
                    <button
                      onClick={() => removeBucket(b.id, b.name)}
                      disabled={busy || isReadOnly}
                      className="text-red-600 hover:underline disabled:text-slate-400 disabled:no-underline text-xs"
                    >
                      Entfernen
                    </button>
                  </td>
                </tr>
              ))}
              {(c?.buckets.length ?? 0) === 0 && (
                <tr><td colSpan={5} className="p-3 text-center text-slate-400">Keine Buckets.</td></tr>
              )}
            </tbody>
          </table>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Name</label>
              <input
                value={bucketName}
                onChange={(e) => setBucketName(e.target.value.toLowerCase())}
                disabled={isReadOnly}
                placeholder="acme-nextcloud-primary"
                className="px-2 py-1 rounded border font-mono text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Zweck</label>
              <input
                value={bucketPurpose}
                onChange={(e) => setBucketPurpose(e.target.value)}
                disabled={isReadOnly}
                placeholder="nextcloud-primary"
                className="px-2 py-1 rounded border text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Region (optional)</label>
              <input
                value={bucketRegion}
                onChange={(e) => setBucketRegion(e.target.value)}
                disabled={isReadOnly}
                placeholder="eu-central-1"
                className="px-2 py-1 rounded border font-mono text-sm"
              />
            </div>
            <button onClick={addBucket} disabled={busy || isReadOnly} className="btn-primary">
              Bucket anlegen
            </button>
          </div>
        </div>

        {isReadOnly && (
          <p className="text-xs text-slate-400">Read-only-Operator; Vertrag kann nicht bearbeitet werden.</p>
        )}
      </div>
    </section>
  );
}
